import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CashBoxPicker from "@/components/CashBoxPicker";
import { Plus, X, Wallet, ClipboardCheck, Printer } from "lucide-react";

const EXPENSE_CATEGORIES = ["إيجار", "رواتب", "كهرباء", "مياه", "مواصلات", "صيانة", "قرطاسية", "تسويق", "ضيافة", "أخرى"];
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("ar-EG");

export default function QuickActions() {
  const { currentCompany, user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  // Quick expense state
  const [exp, setExp] = useState({ category: "أخرى", amount: "", description: "", date: todayStr() });
  const [box, setBox] = useState({ currency: null, cash_box_id: null });
  const [saving, setSaving] = useState(false);

  // Daily closing state
  const [closing, setClosing] = useState(null);
  const [counted, setCounted] = useState({});
  const [loadingClose, setLoadingClose] = useState(false);

  useEffect(() => {
    const onShortcut = (e) => {
      if (e.detail === "expense") { setExpOpen(true); setMenuOpen(false); }
      if (e.detail === "closing") openClosing();
    };
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "F4") { e.preventDefault(); setExpOpen(true); setMenuOpen(false); }
      if (e.key === "F5") { e.preventDefault(); openClosing(); }
    };
    window.addEventListener("muhasabaty-quick-action", onShortcut);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("muhasabaty-quick-action", onShortcut); window.removeEventListener("keydown", onKey); };
  }, []);

  const allowed = ["super_admin", "company_manager", "accountant"].includes(user?.role);
  if (!allowed) return null;

  const submitExpense = async () => {
    if (!exp.amount || Number(exp.amount) <= 0) { toast.error("أدخل مبلغاً صحيحاً"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", {
        category: exp.category, amount: Number(exp.amount),
        currency: (box.currency || currentCompany?.base_currency || "SYP"),
        date: exp.date, description: exp.description, cash_box_id: box.cash_box_id || null,
      });
      toast.success("تم تسجيل المصروف");
      setExp({ category: "أخرى", amount: "", description: "", date: todayStr() });
      setExpOpen(false); setMenuOpen(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const openClosing = async () => {
    setMenuOpen(false); setCloseOpen(true); setClosing(null); setCounted({}); setLoadingClose(true);
    try { const { data } = await api.get("/reports/daily-closing"); setClosing(data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoadingClose(false); }
  };

  const printClosing = () => {
    if (!closing) return;
    const rows = (closing.cash_boxes || []).map(b => {
      const c = counted[b.id];
      const diff = c === undefined || c === "" ? null : Number(c) - b.balance;
      return `<tr><td>${b.name}</td><td>${b.currency}</td><td style="text-align:left">${fmt(b.balance)}</td><td style="text-align:left">${c === undefined || c === "" ? "—" : fmt(c)}</td><td style="text-align:left;color:${diff === null ? "#666" : diff === 0 ? "#059669" : "#dc2626"}">${diff === null ? "—" : (diff > 0 ? "+" : "") + fmt(diff)}</td></tr>`;
    }).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html dir="rtl"><head><meta charset="utf-8"><title>تقفيل اليومية</title>
      <style>body{font-family:Tajawal,Arial;padding:24px}h2{margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ddd;padding:8px;font-size:13px}th{background:#f1f5f9}.s{display:flex;gap:12px;flex-wrap:wrap;margin-top:12px}.c{border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;min-width:120px}.c b{display:block;font-size:18px}</style></head><body>
      <h2>تقفيل اليومية — ${currentCompany?.name || ""}</h2><div>التاريخ: ${closing.date}</div>
      <div class="s">
        <div class="c">مبيعات اليوم<b>${fmt(closing.sales_total)}</b>${closing.sales_count} فاتورة</div>
        <div class="c">نقدي<b>${fmt(closing.cash_sales)}</b></div>
        <div class="c">آجل<b>${fmt(closing.credit_sales)}</b></div>
        <div class="c">مرتجعات<b>${fmt(closing.returns_total)}</b></div>
        <div class="c">مصروفات<b>${fmt(closing.expenses_total)}</b></div>
      </div>
      <table><thead><tr><th>الصندوق</th><th>العملة</th><th>رصيد النظام</th><th>المعدود</th><th>الفرق</th></tr></thead><tbody>${rows}</tbody></table>
      <p style="margin-top:24px;font-size:12px;color:#666">أُنشئ عبر محاسبتي — ${new Date().toLocaleString("ar-EG")}</p>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <>
      {/* Floating action button */}
      <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-2" data-testid="quick-actions">
        {menuOpen && (
          <>
            <Button onClick={() => { setExpOpen(true); setMenuOpen(false); }} className="gap-2 shadow-lg bg-rose-600 hover:bg-rose-700 h-11 rounded-full" data-testid="quick-expense-open">
              <Wallet className="w-4 h-4" /> مصروف سريع
            </Button>
            <Button onClick={openClosing} className="gap-2 shadow-lg bg-indigo-600 hover:bg-indigo-700 h-11 rounded-full" data-testid="daily-closing-open">
              <ClipboardCheck className="w-4 h-4" /> تقفيل اليومية
            </Button>
          </>
        )}
        <button
          onClick={() => setMenuOpen(o => !o)}
          className={`w-14 h-14 rounded-full shadow-xl grid place-items-center text-white transition-all duration-300 ${menuOpen ? "bg-slate-700 rotate-45" : "bg-primary hover:brightness-110"}`}
          data-testid="quick-actions-fab"
          title="إجراءات سريعة"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Quick expense dialog */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent className="max-w-md" data-testid="quick-expense-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-rose-600" /> تسجيل مصروف سريع</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">الفئة</label>
                <Select value={exp.category} onValueChange={v => setExp({ ...exp, category: v })}>
                  <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">المبلغ</label>
                <Input type="number" value={exp.amount} onChange={e => setExp({ ...exp, amount: e.target.value })} placeholder="0" data-testid="expense-amount" autoFocus />
              </div>
            </div>
            <CashBoxPicker value={box} onChange={patch => setBox({ ...box, ...patch })} company={currentCompany} compact data-testid="expense-cashbox" />
            <div>
              <label className="text-xs font-medium">ملاحظة (اختياري)</label>
              <Input value={exp.description} onChange={e => setExp({ ...exp, description: e.target.value })} placeholder="وصف المصروف" data-testid="expense-description" />
            </div>
            <div>
              <label className="text-xs font-medium">التاريخ</label>
              <Input type="date" value={exp.date} onChange={e => setExp({ ...exp, date: e.target.value })} data-testid="expense-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpOpen(false)}>إلغاء</Button>
            <Button onClick={submitExpense} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="expense-submit">{saving ? "جارٍ..." : "تسجيل المصروف"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Daily closing dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-lg" data-testid="daily-closing-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-indigo-600" /> تقفيل اليومية {closing?.date ? `— ${closing.date}` : ""}</DialogTitle></DialogHeader>
          {loadingClose ? (
            <div className="py-8 text-center text-muted-foreground text-sm">جارٍ التحميل...</div>
          ) : !closing ? null : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border p-2 bg-emerald-50/50"><div className="text-[10px] text-muted-foreground">مبيعات ({closing.sales_count})</div><div className="font-bold text-emerald-700">{fmt(closing.sales_total)}</div></div>
                <div className="rounded-lg border p-2 bg-blue-50/50"><div className="text-[10px] text-muted-foreground">نقدي</div><div className="font-bold text-blue-700">{fmt(closing.cash_sales)}</div></div>
                <div className="rounded-lg border p-2 bg-amber-50/50"><div className="text-[10px] text-muted-foreground">آجل</div><div className="font-bold text-amber-700">{fmt(closing.credit_sales)}</div></div>
                <div className="rounded-lg border p-2 bg-slate-50"><div className="text-[10px] text-muted-foreground">مرتجعات</div><div className="font-bold text-slate-700">{fmt(closing.returns_total)}</div></div>
                <div className="rounded-lg border p-2 bg-rose-50/50 col-span-2"><div className="text-[10px] text-muted-foreground">مصروفات اليوم</div><div className="font-bold text-rose-700">{fmt(closing.expenses_total)}</div></div>
              </div>

              <div>
                <div className="text-xs font-bold mb-1.5">جرد الصناديق — أدخل النقد المعدود فعلياً:</div>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {(closing.cash_boxes || []).map(b => {
                    const c = counted[b.id];
                    const diff = c === undefined || c === "" ? null : Number(c) - b.balance;
                    return (
                      <div key={b.id} className="flex items-center gap-2 p-2 rounded-lg border" data-testid={`closing-box-${b.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{b.name}</div>
                          <div className="text-[11px] text-muted-foreground">رصيد النظام: <b>{fmt(b.balance)} {b.currency}</b></div>
                        </div>
                        <Input type="number" value={c ?? ""} onChange={e => setCounted({ ...counted, [b.id]: e.target.value })} placeholder="المعدود" className="w-24 h-9 text-sm" data-testid={`closing-counted-${b.id}`} />
                        <div className={`w-20 text-center text-xs font-bold ${diff === null ? "text-muted-foreground" : diff === 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`closing-diff-${b.id}`}>
                          {diff === null ? "—" : (diff > 0 ? "+" : "") + fmt(diff)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">الفرق = المعدود − رصيد النظام (٠ = مطابق، أحمر = عجز/زيادة)</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>إغلاق</Button>
            <Button onClick={printClosing} disabled={!closing} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="closing-print"><Printer className="w-4 h-4" /> طباعة</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
